import { useEffect, useRef, useState } from 'react';
import { Button } from './Button.tsx';
import type { RequestPaymentKind } from '@dermalink/shared';
import styles from './CloseConversationDialog.module.css';

interface CloseConversationDialogProps {
  /** When false, only the free option is selectable — Mollie isn't live yet for this account. */
  paymentsEnabled?: boolean;
  confirming?: boolean;
  onConfirm: (kind: RequestPaymentKind) => void;
  onCancel: () => void;
}

export function CloseConversationDialog({ paymentsEnabled = true, confirming = false, onConfirm, onCancel }: CloseConversationDialogProps) {
  const [kind, setKind] = useState<RequestPaymentKind | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !confirming) onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirming, onCancel]);

  const options: { value: RequestPaymentKind; label: string; description: string; requiresPayments: boolean }[] = [
    {
      value:            'follow_up',
      label:            'Opvolgconsult — € 0',
      description:      'Geen extra bijdrage. De patiënt wordt gevraagd het consult te bevestigen en te beoordelen.',
      requiresPayments: false,
    },
    {
      value:            'prescription',
      label:            'Enkel voorschrift — € 5',
      description:      'De patiënt ontvangt een betalingsverzoek van € 5. Na betaling wordt het consult afgerond en gevraagd om een beoordeling.',
      requiresPayments: true,
    },
    {
      value:            'new_consult',
      label:            'Nieuw consult — € 32',
      description:      'De patiënt ontvangt een betalingsverzoek van € 32. Na betaling wordt het consult afgerond en gevraagd om een beoordeling.',
      requiresPayments: true,
    },
  ];

  return (
    <div
      className={styles.overlay}
      onClick={() => { if (!confirming) onCancel(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="close-conversation-title"
    >
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <h2 id="close-conversation-title" className={styles.title}>Consult afronden</h2>
        <p className={styles.message}>
          Kies hoe dit consult aangerekend wordt. De patiënt ontvangt een verzoek om te bevestigen (en indien nodig te betalen), en wordt na afronding gevraagd het consult te beoordelen.
        </p>
        <div className={styles.options} role="radiogroup" aria-label="Type consult">
          {options.map((opt) => {
            const optionDisabled = confirming || (opt.requiresPayments && !paymentsEnabled);
            return (
              <label key={opt.value} className={`${styles.option}${optionDisabled ? ` ${styles.optionDisabled}` : ''}`}>
                <input
                  type="radio"
                  name="close-conversation-kind"
                  value={opt.value}
                  checked={kind === opt.value}
                  disabled={optionDisabled}
                  onChange={() => setKind(opt.value)}
                />
                <span className={styles.optionBody}>
                  <span className={styles.optionLabel}>{opt.label}</span>
                  <span className={styles.optionDescription}>
                    {opt.requiresPayments && !paymentsEnabled ? 'Online betalen is nog niet actief voor dit account.' : opt.description}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
        <div className={styles.actions}>
          <Button ref={cancelRef} variant="ghost" onClick={onCancel} disabled={confirming}>Annuleren</Button>
          <Button variant="primary" onClick={() => { if (kind) onConfirm(kind); }} disabled={confirming || kind === null}>
            Bevestigen
          </Button>
        </div>
      </div>
    </div>
  );
}
